// === INBOEDEL SCREEN WITH CUSTOM FIELDS ===
// Converted from JsonForms to custom fields
// Combines inboedel-basic and inboedel-coverage into single page
// Similar structure to WoonhuisScreen.tsx

import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  useWorkflow,
  useSharedAddress,
  useConfig,
  WorkflowPageLayout,
  getNextScreen,
  getPreviousScreen,
  formatPrice,
  bffClient,
  LoadingScreen,
  computeInboedelDisplayPremium,
  resolveCommissionRate,
  getConfigPathForBrand,
  getCurrentBrand,
  DatePicker,
} from '@sluitstraat/core';
import { getApplicationName } from '../../utils/appName';
import { inboedelStyles as styles } from '../../styles/commonStyles';
import { WorkflowNavButtons } from '../../components/WorkflowNavButtons';
import { useWorkflowNav } from '../../hooks/useWorkflowNav';
import {
  findSiblingRiskAddress,
  findUpstreamAddress,
  findUpstreamRiskAddress,
  shouldHideAddressSection,
  isSameBuilding,
  copyBuildingDetails,
  vpAddressAsBa,
  stripInfofolioUiState,
  syncAddressToOtherProducts,
  syncRiskAddressToProperties,
  INFOFOLIO_DERIVED_BA_FIELDS,
  INFOFOLIO_SESSION_KEYS,
} from '../../utils/addressSharing';
import { useSharedRiskAddress } from '../../hooks/useSharedRiskAddress';
import { useDeferredCommit } from '../../hooks/useDeferredCommit';
import { readFamilyComposition, findSharedFamilyComposition } from '../../utils/familyComposition';
import { DebugPanel } from '../../components/DebugPanel';
import { WithHelpSidebar } from '../../components/HelpCard';
import { WoningSpecificaties } from '../../components/WoningSpecificaties';
import { InfoTooltip } from '../../components/InfoTooltip';
import AddressSection, { Address } from '../../components/AddressSection';
import { shouldShowCoverageSection } from './inboedel/coverageSection';
import { ClipboardText, Users, Shield, CurrencyEur, House } from 'phosphor-react';
import { Check } from 'lucide-react';
import { stableStringify } from '../../utils/stableStringify';
import { INBOEDEL_INFOFOLIO_FIELDS_KEY, inboedelGlassAllowed, inboedelGlassNeedsContact, infofolioAddressKey } from '../../utils/glassPremium';
import {
  EC_CODE_MOBIEL,
  EC_CODE_SMARTHOME,
  INBOEDEL_OPTIONAL_COVERAGES,
  getEc,
  applyInboedelDiscount,
  mapToSelectedCoveragesRequest,
  mapFromCalculationResponse,
  cleanUnselectedCoveragePremiums,
  extractDefaults,
  deepMerge,
  applyProductDefaults,
  formatCurrency,
  unformatCurrency,
  linkRelationData,
  linkWoonhuisAddress,
  validateInboedel,
  scrollToFirstError,
  applyInboedelFieldUpdate,
  useCoverageCalculation,
  inboedelCalcInputSignature,
  restampCoveragePricesCache,
  inboedelPreviewCacheKey,
  useInfofolio,
  useUpstreamSync,
  UninsurableWarning,
  CoverageToggleCard,
} from './inboedel';

const SCREEN_ID = 'inboedel';

// Address helpers for the two AddressSections. The wire stays as it was: BA_* strings,
// VP_HUISNR numeric.
const addressAsBa = (a: Address) => ({
  BA_PCODE: a.postcode,
  BA_HUISNR: a.houseNumber,
  BA_TOEVOEG: a.addition || '',
  BA_STRAAT: a.street || '',
  BA_PLAATS: a.city || '',
});
const baIdentity = (ba: any) => `${ba?.BA_PCODE || ''}|${ba?.BA_HUISNR ?? ''}|${ba?.BA_TOEVOEG || ''}`;

export const InboedelScreen: React.FC = () => {
  const SHOW_OPTIONAL_COVERAGES = true;

  const navigate = useNavigate();
  const { t } = useTranslation();
  const { getStepData, saveStepData, selectedProducts, relationData } = useWorkflow();
  const { config } = useConfig();

  // UI-only ex-provision display price for a single coverage section — same formula as core's
  // computeInboedelSelectedTotal so the "+€X" optional badges add up to the section total.
  // The section code drives both the discount exclusion and the commission rate, so the screen
  // can never disagree with core's excludeCoverages / commissionRates config.
  const sectionDisplayPrice = (btp: number | string | undefined, section: string, sec?: any): number => {
    const n = Number(btp) || 0;
    if (n <= 0) return 0;
    const disc = (config as any)?.inboedelDiscount;
    const excluded = (disc?.excludeCoverages || []).includes(section);
    // Mirror computeInboedelDisplayPremium's discount logic: only apply the factor when the
    // discount is configured as a UI overlay (applyToData=false). When applyToData=true (SAA),
    // the InboedelScreen bake has already baked the 5% into the stored BTP, so applying it
    // here would double-discount (LS 2.52 → bake 2.39 → helper 2.39 × 0.95 = 2.27 ≈ €2,19
    // instead of the correct 2.39 × 0.8 × 1.21 = €2,31).
    const discActive = disc?.enabled && (disc.percentage ?? 0) > 0 && disc?.applyToData === false;
    const factor = discActive && !excluded ? 1 - disc.percentage / 100 : 1;
    const pprc = resolveCommissionRate(section, sec, disc);
    // Floor to 2 decimals — matches AVP/Inboedel display helpers' round-down rule.
    return Math.floor(n * factor * (1 - pprc / 100) * 1.21 * 100) / 100;
  };
  // Schema and loading states
  const [, setSchema] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [validationErrors, setValidationErrors] = useState<Record<string, string>>({});

  // Data state - use lazy initializer to prevent re-running during re-renders
  const [data, setData] = useState<any>(() => getStepData(SCREEN_ID) || {});

  // Shared address hook - single source of truth for address data
  const { address: sharedAddress, updateAddress: updateSharedAddress } = useSharedAddress();
  const { riskAddressSame, riskAddressSource, updateRiskAddress, setRiskAddressSame } = useSharedRiskAddress();
  const { stage: stageAddressSave, schedule: scheduleAddressSave } = useDeferredCommit();

  // Risk address toggle - 'J' means insured property address = policyholder address. Own stored
  // answer first, then the shared one (both property products insure the same building).
  const [risicoAdresGelijk, setRisicoAdresGelijk] = useState(() => {
    return data?.Supportsection?.Divers?.RisicoAdresGelijk || riskAddressSame || 'J';
  });

  // Last risk-address identity (pcode|huisnr|toevoeg) we propagated to the shared store, so the
  // propagation effect only fires on a real change and never echoes a value that flowed in.
  const lastPropagatedAddress = useRef<string>('');

  // Rented-property detection: BFF rejects inboedel when BA_WONING is 'D' (deels verhuurd) or 'H' (verhuurd aan derden).
  // Derived directly from data (no separate state) so it always tracks the current selection.
  const isRentedProperty = (() => {
    const woning = data?.Contractdocument?.PP?.BA?.BA_WONING;
    return woning === 'D' || woning === 'H';
  })();

  // Mobile keyboard dismissal detection - triggers blur when keyboard closes
  // This fixes the issue where dismissing the keyboard doesn't trigger onBlur
  useEffect(() => {
    if (typeof window === 'undefined' || !window.visualViewport) return;

    let lastHeight = window.visualViewport.height;

    const handleViewportResize = () => {
      const currentHeight = window.visualViewport?.height || 0;
      // Keyboard dismissed when viewport height increases significantly (> 100px)
      if (currentHeight - lastHeight > 100) {
        const activeElement = document.activeElement as HTMLInputElement;
        if (activeElement && (activeElement.tagName === 'INPUT' || activeElement.tagName === 'TEXTAREA')) {
          activeElement.blur();
        }
      }
      lastHeight = currentHeight;
    };

    window.visualViewport.addEventListener('resize', handleViewportResize);
    return () => {
      window.visualViewport?.removeEventListener('resize', handleViewportResize);
    };
  }, []);

  // Contact info from config for monument warning
  const [contactInfo, setContactInfo] = useState<{ phone: string; email: string }>({
    phone: '',
    email: ''
  });

  // Inboedel discount config (hidden discount on non-glass coverages)
  const inboedelDiscountRef = useRef<{ enabled: boolean; percentage: number; excludeCoverages: string[]; applyToData: boolean }>({
    enabled: false, percentage: 0, excludeCoverages: [], applyToData: true
  });

  // Store undiscounted data for workflow persistence (when applyToData is false)
  const undiscountedDataRef = useRef<any>(null);

  // Wrapper: saves undiscounted premiums to workflow when applyToData is false
  // The ref is only populated when discount is active AND applyToData is false
  const saveWorkflowData = useCallback((stepId: string, displayData: any) => {
    if (undiscountedDataRef.current?.Contractdocument?.PP) {
      const saveData = JSON.parse(JSON.stringify(displayData));
      const srcPP = undiscountedDataRef.current.Contractdocument.PP;
      const dstPP = saveData?.Contractdocument?.PP;
      if (dstPP && srcPP) {
        // Copy all section-level and PP-level premium fields from undiscounted source
        for (const s of ['IP', 'CY', 'BI', 'GP', 'LS', 'OP', 'HU', 'KB']) {
          const src = Array.isArray(srcPP[s]) ? srcPP[s][0] : srcPP[s];
          const dst = Array.isArray(dstPP[s]) ? dstPP[s][0] : dstPP[s];
          if (src && dst) {
            for (const suffix of ['_NTP', '_TPP', '_TASS', '_BTP', '_TTOT']) {
              const key = `${s}${suffix}`;
              if (src[key] !== undefined) dst[key] = src[key];
            }
          }
        }
        if (Array.isArray(srcPP.EC) && Array.isArray(dstPP.EC)) {
          for (let i = 0; i < srcPP.EC.length && i < dstPP.EC.length; i++) {
            for (const suffix of ['_NTP', '_TPP', '_TASS', '_BTP', '_TTOT']) {
              const key = `EC${suffix}`;
              if (srcPP.EC[i][key] !== undefined) dstPP.EC[i][key] = srcPP.EC[i][key];
            }
          }
        }
        for (const k of ['PP_NTP', 'PP_TPP', 'PP_TASS', 'PP_BTP', 'PP_TTOT', 'PP_NTPVPK']) {
          if (srcPP[k] !== undefined) dstPP[k] = srcPP[k];
        }
      }
      saveStepData(stepId, saveData);
    } else {
      saveStepData(stepId, displayData);
    }
  }, [saveStepData]);

  // Infofolio machinery (property-info fetch incl. IB_DAGWRDE -> inboedelwaarde, monument flag,
  // infofolio field locks, pending-address trigger, address-change check) — see the hook.
  // Called BEFORE useUpstreamSync, which consumes setInfofolioFilledFields + clearInfofolioTracking.
  const {
    isMonumentProperty,
    setIsMonumentProperty,
    isLoadingPropertyInfo,
    infofolioFilledFields,
    setInfofolioFilledFields,
    infofolioLookupDoneFor,
    clearInfofolioTracking,
    checkAddressChangedAndTrigger,
  } = useInfofolio({
    selectedProducts,
    getStepData,
    saveStepData,
    saveWorkflowData,
    setData,
    setValidationErrors,
  });

  // May this address carry glass right now: config switch off, no selected product already
  // covering glass, and Infofolio supplied the herbouwwaarde + inboedelwaarde (Midglas declines
  // addresses its own source does not know and takes the WHOLE calculation down with it).
  const glassAllowed = inboedelGlassAllowed(config, selectedProducts, infofolioFilledFields);
  // Unknown address and no Woonhuis carrying glass: the tile is replaced by a "contact DFD" note,
  // but only once Infofolio has answered for THIS address (never before an address is entered,
  // never while the lookup is in flight, never for a stale answer after an address change).
  const glassNeedsContact = inboedelGlassNeedsContact(config, selectedProducts, infofolioFilledFields)
    && !isLoadingPropertyInfo
    && infofolioLookupDoneFor !== ''
    && infofolioLookupDoneFor === infofolioAddressKey(data?.Contractdocument?.PP?.BA);
  // ...and the same answer as a ref, for the CALCULATION path. ConfigProvider renders its
  // children on DEFAULT_CONFIG while /config.json is still in flight, and the auto-calc is
  // debounced: a run scheduled before the real config landed holds a callback captured with
  // the wrong value, and adding a dependency cannot change what an already-pending timer will
  // call. Only a ref, read at call time, lets that run see the switch, and getting this wrong
  // puts GP on the wire, which costs the customer every price on the screen, not just glass.
  // Same reason inboedelDiscountRef exists.
  const glassDisabledRef = useRef(!glassAllowed);
  useEffect(() => {
    glassDisabledRef.current = !glassAllowed;
  }, [glassAllowed]);

  // Upstream sync (shared family composition + Woonhuis lock states + Glas force-off) — see the hook.
  const {
    familyComposition,
    setFamilyComposition,
    isAddressLocked,
    setIsAddressLocked,
    isBuildingDetailsLocked,
    setIsBuildingDetailsLocked,
    isGezinsamenstellingLocked,
    setIsGezinsamenstellingLocked,
  } = useUpstreamSync({
    screenId: SCREEN_ID,
    data,
    setData,
    getStepData,
    saveStepData,
    selectedProducts,
    config,
    setInfofolioFilledFields,
    clearInfofolioTracking,
    glassDisabled: !glassAllowed,
    handleCustomFieldChange: (fieldPath, value) => handleCustomFieldChange(fieldPath, value),
  });

  // Premium calculation machinery (auto-calc effect + handleCalculate) — see the hook.
  const {
    isCalculating,
    setIsCalculating,
    calculationError,
    calculationSuccess,
    setCalculationSuccess,
    lastCalculationInput,
    lastCalculationOutput,
    setShouldAutoCalculate,
    initialLoadComplete,
    isUpdatingFromCalculation,
    lastCalculatedData,
    handleCalculate,
    previewPrices,
  } = useCoverageCalculation({
    screenId: SCREEN_ID,
    data,
    setData,
    saveWorkflowData,
    t,
    setValidationErrors,
    loading,
    isLoadingPropertyInfo,
    isMonumentProperty,
    isRentedProperty,
    isAddressLocked,
    isBuildingDetailsLocked,
    isGezinsamenstellingLocked,
    isGlassRebuildValueLocked: infofolioFilledFields.has('herbwrd'),
    inboedelDiscountRef,
    undiscountedDataRef,
    glassDisabledRef,
  });

  const previewPp: Record<string, any> | null = previewPrices
    ? { ...previewPrices.sections, EC: previewPrices.ec }
    : null;


  // Validate required data on mount - redirect if missing critical data
  useEffect(() => {
    // Check if inboedel product is selected
    if (!selectedProducts?.inboedel) {
      navigate('/workflow/product-selection', { replace: true });
      return;
    }

    // Check if client information exists
    const clientInfo = relationData?.clientInfo;
    if (!clientInfo || !clientInfo.email || !clientInfo.initials || !clientInfo.lastName) {
      navigate('/workflow/client-information', { replace: true });
      return;
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps -- mount-only guard; re-running on selectedProducts/navigate identity would redirect mid-flow
  }, []); // Run once on mount

  // Load contact info from config for monument warning
  useEffect(() => {
    const loadContactInfo = async () => {
      try {
        const response = await fetch(getConfigPathForBrand(getCurrentBrand()));
        const config = await response.json();
        const phone = config.texts?.contact?.phone || config.customer?.contact?.phone?.main || '';
        const email = config.texts?.contact?.email || config.customer?.contact?.email || '';
        setContactInfo({ phone, email });

        // Load inboedel discount config
        if (config.inboedelDiscount?.enabled) {
          inboedelDiscountRef.current = {
            enabled: true,
            percentage: config.inboedelDiscount.percentage || 0,
            excludeCoverages: config.inboedelDiscount.excludeCoverages || [],
            applyToData: config.inboedelDiscount.applyToData !== false
          };
        }
      } catch (error) {
        // Fallback values if config fails to load
        console.warn('Failed to load contact info from config');
      }
    };
    loadContactInfo();
  }, []);

  // Load schema and initialize data on mount
  useEffect(() => {
    const loadSchema = async () => {
      try {

        const response = await fetch(`/screens/${SCREEN_ID}.json`);
        if (!response.ok) {
          throw new Error(`Failed to load schema for ${SCREEN_ID}`);
        }

        const screenData = await response.json();
        const loadedSchema = screenData.jsonFormSchema;

        setSchema(loadedSchema);

        // Extract defaults from schema
        const schemaDefaults = extractDefaults(loadedSchema);

        // Get existing data
        const existingData = getStepData(SCREEN_ID) || {};

        // Unwrap IP array before merge (cleanInboedelForSchema saves IP as array for schema compliance,
        // but deepMerge doesn't handle arrays properly - it spreads to {0: {...}} which gets deleted later)
        if (Array.isArray(existingData?.Contractdocument?.PP?.IP)) {
          existingData.Contractdocument.PP.IP = existingData.Contractdocument.PP.IP[0] || {};
        }

        // Deep merge: existing data takes precedence over schema defaults
        const mergedData = deepMerge(existingData, schemaDefaults);

        // Apply product-specific defaults (appName/baseUrl/now injected; calc.ts stays pure)
        const appName = await getApplicationName("NU'91");
        const now = new Date();
        const dataWithProductDefaults = applyProductDefaults(mergedData, { appName, baseUrl: window.location.origin + '/', now });

        // Clear stored street and city values if not provided by relation data OR existing saved data
        // This prevents old test/cached values from appearing, but preserves user-entered/looked-up data
        // NOTE: Inboedel is now FIRST for address fields (Aansprakelijkheid doesn't have address), so no need to check other products
        const existingBA = existingData?.Contractdocument?.PP?.BA;

        // Only delete street if it's not in relation data AND not in existing saved data
        if (!relationData?.clientInfo?.street && !existingBA?.BA_STRAAT) {
          if (dataWithProductDefaults?.Contractdocument?.PP?.BA) {
            delete dataWithProductDefaults.Contractdocument.PP.BA.BA_STRAAT;
          }
        }

        // Only delete city if it's not in relation data AND not in existing saved data
        if (!relationData?.clientInfo?.city && !existingBA?.BA_PLAATS) {
          if (dataWithProductDefaults?.Contractdocument?.PP?.BA) {
            delete dataWithProductDefaults.Contractdocument.PP.BA.BA_PLAATS;
          }
        }

        // Link relation data from client-information page
        const dataWithRelationData = linkRelationData(dataWithProductDefaults, relationData, { now });

        const woonhuisStep = getStepData('woonhuis');
        const woonhuisStepBA = woonhuisStep?.Contractdocument?.PP?.BA;
        const canReuseWoonhuis = selectedProducts?.woonhuis
          || (woonhuisStepBA?.BA_PCODE && sharedAddress?.BA_PCODE
            && String(woonhuisStepBA.BA_PCODE) === String(sharedAddress.BA_PCODE)
            && String(woonhuisStepBA.BA_HUISNR) === String(sharedAddress.BA_HUISNR));
        const woonhuisData = canReuseWoonhuis ? woonhuisStep : null;
        let dataWithWoonhuisData = linkWoonhuisAddress(dataWithRelationData, woonhuisData);

        // sharedAddress is the canonical policyholder address — always overwrite local BA
        // (and VP, since this is the policyholder) with it when populated, so an edit made
        // elsewhere flows in here on every visit. Falls back to scanning other products'
        // step data only when sharedAddress hasn't been seeded yet.
        const sourceBA = (sharedAddress?.BA_PCODE && sharedAddress?.BA_HUISNR)
          ? sharedAddress
          : findUpstreamAddress(getStepData, SCREEN_ID);
        if (sourceBA?.BA_PCODE && sourceBA?.BA_HUISNR) {
          const huisnrNum = Number(sourceBA.BA_HUISNR);
          const vpHuisnr = isNaN(huisnrNum) ? 0 : huisnrNum;
          // Only mirror into BA when the risk address equals the policyholder address.
          // When risicoAdresGelijk === 'N' the local BA is a separate risk address that
          // shouldn't be overwritten by the canonical policyholder one.
          const syncBA = (dataWithWoonhuisData?.Supportsection?.Divers?.RisicoAdresGelijk ?? 'J') === 'J';
          dataWithWoonhuisData = {
            ...dataWithWoonhuisData,
            Contractdocument: {
              ...dataWithWoonhuisData.Contractdocument,
              PP: {
                ...dataWithWoonhuisData.Contractdocument?.PP,
                ...(syncBA ? {
                  BA: {
                    ...dataWithWoonhuisData.Contractdocument?.PP?.BA,
                    BA_PCODE: sourceBA.BA_PCODE,
                    BA_HUISNR: sourceBA.BA_HUISNR,
                    BA_TOEVOEG: sourceBA.BA_TOEVOEG || '',
                    BA_STRAAT: sourceBA.BA_STRAAT || '',
                    BA_PLAATS: sourceBA.BA_PLAATS || '',
                  },
                } : {}),
                VP: {
                  ...dataWithWoonhuisData.Contractdocument?.PP?.VP,
                  VP_PCODE: sourceBA.BA_PCODE,
                  VP_HUISNR: vpHuisnr,
                  VP_TOEVOEG: sourceBA.BA_TOEVOEG || '',
                  VP_STRAAT: sourceBA.BA_STRAAT || '',
                  VP_PLAATS: sourceBA.BA_PLAATS || '',
                },
              },
            },
          };
        }

        // The risk-address answer: own stored value (linkWoonhuisAddress already carried Woonhuis's
        // over), then the shared one. On 'N' the insured risk address is a sibling's when this
        // product has none of its own yet (its BA still mirrors the policyholder address).
        const effectiveRisico: 'J' | 'N' =
          dataWithWoonhuisData?.Supportsection?.Divers?.RisicoAdresGelijk === 'N' || (!dataWithWoonhuisData?.Supportsection?.Divers?.RisicoAdresGelijk && riskAddressSame === 'N')
            ? 'N' : 'J';
        setRisicoAdresGelijk(effectiveRisico);
        if (effectiveRisico === 'N') {
          const localBA = dataWithWoonhuisData?.Contractdocument?.PP?.BA;
          const mirrorsPolicyholder = !localBA?.BA_PCODE
            || baIdentity(localBA) === baIdentity(vpAddressAsBa(dataWithWoonhuisData?.Contractdocument?.PP?.VP));
          const upstreamRisk = mirrorsPolicyholder ? findUpstreamRiskAddress(getStepData, SCREEN_ID) : null;
          if (upstreamRisk) {
            dataWithWoonhuisData = {
              ...dataWithWoonhuisData,
              Contractdocument: {
                ...dataWithWoonhuisData.Contractdocument,
                PP: {
                  ...dataWithWoonhuisData.Contractdocument?.PP,
                  BA: {
                    ...dataWithWoonhuisData.Contractdocument?.PP?.BA,
                    BA_PCODE: upstreamRisk.BA_PCODE,
                    BA_HUISNR: upstreamRisk.BA_HUISNR,
                    BA_TOEVOEG: upstreamRisk.BA_TOEVOEG || '',
                    BA_STRAAT: upstreamRisk.BA_STRAAT || '',
                    BA_PLAATS: upstreamRisk.BA_PLAATS || '',
                  },
                },
              },
            };
          }
        }
        dataWithWoonhuisData = {
          ...dataWithWoonhuisData,
          Supportsection: {
            ...dataWithWoonhuisData?.Supportsection,
            Divers: { ...dataWithWoonhuisData?.Supportsection?.Divers, RisicoAdresGelijk: effectiveRisico },
          },
        };

        // Check if Gezinssamenstelling is locked (filled in Woonhuis, which comes before Inboedel)
        const woonhuisGSS = woonhuisData?.Contractdocument?.PP?.OP?.OP_GSS;

        // Address is editable everywhere — never lock it.
        setIsAddressLocked(false);

        // Building details are reused from Woonhuis only when it insures the SAME building
        // (matching risk address), so a divergent risicoadres never reuses the wrong building.
        const woonhuisBA = woonhuisData?.Contractdocument?.PP?.BA;
        const inboedelBA = dataWithWoonhuisData?.Contractdocument?.PP?.BA;
        const woonhuisHasBuildingData = woonhuisBA?.BA_TYPEWON;
        const isBuildingDetailsLockedNow = Boolean(
          selectedProducts?.woonhuis && woonhuisHasBuildingData && isSameBuilding(woonhuisBA, inboedelBA),
        );
        setIsBuildingDetailsLocked(isBuildingDetailsLockedNow);

        // Reuse the shared building characteristics (oppervlakte, bouwjaar, type, wall/roof, ...)
        // so they are entered once. The inputs are hidden while locked; copying the values keeps
        // the premium calculation correct.
        if (isBuildingDetailsLockedNow) {
          dataWithWoonhuisData = {
            ...dataWithWoonhuisData,
            Contractdocument: {
              ...dataWithWoonhuisData.Contractdocument,
              PP: {
                ...dataWithWoonhuisData.Contractdocument.PP,
                BA: copyBuildingDetails(woonhuisBA, inboedelBA),
              },
            },
          };
        }

        if (selectedProducts?.woonhuis && woonhuisGSS) {
          setIsGezinsamenstellingLocked(true);

          // Copy the value from Woonhuis to Inboedel
          if (!dataWithWoonhuisData.Contractdocument?.PP?.IP) {
            dataWithWoonhuisData.Contractdocument.PP.IP = {};
          }
          dataWithWoonhuisData.Contractdocument.PP.IP.IP_GSS = woonhuisGSS;
        } else {
          setIsGezinsamenstellingLocked(false);
        }

        // When Woonhuis already fetched infofolio and synced values to Inboedel,
        // lock those fields in the infofolio tracking so they appear readonly
        if (isBuildingDetailsLockedNow) {
          const woonhuisInfofolioFields = (() => {
            try {
              const saved = sessionStorage.getItem('woonhuis_infofolio_fields');
              return saved ? new Set(JSON.parse(saved) as string[]) : new Set<string>();
            } catch { return new Set<string>(); }
          })();

          if (woonhuisInfofolioFields.size > 0) {
            setInfofolioFilledFields(prev => {
              const merged = new Set(prev);
              // Map woonhuis field keys to inboedel equivalents
              for (const field of woonhuisInfofolioFields) {
                if (field !== 'permbew') merged.add(field);
              }
              // Woonhuis tracks an Infofolio herbouwwaarde as 'verzsom'; the value reaches this
              // product through linkInboedelAddress, so the lock (and the glass gate) follow it.
              if (woonhuisInfofolioFields.has('verzsom')) merged.add('herbwrd');
              // Lock inboedelwaarde if Woonhuis determined the herbouwwaarde (IB_DAGWRDE was set)
              const ip = dataWithWoonhuisData?.Contractdocument?.PP?.IP;
              const ib = dataWithWoonhuisData?.Contractdocument?.PP?.IB;
              if (ip?.IP_VERZSOM && ib?.IB_INBVST === 'M') {
                merged.add('inboedelwaarde');
                merged.add('inbvst');
                merged.add('herbvst');
              }
              // Persist so it survives further navigation
              sessionStorage.setItem(INBOEDEL_INFOFOLIO_FIELDS_KEY, JSON.stringify(Array.from(merged)));
              return merged;
            });
          }
        }

        // If valid address exists, check if we need to (re)fetch Infofolio data
        // This handles both new addresses and address changes from AVP.
        // checkAddressChangedAndTrigger owns the tracking clears + the deferred lookup trigger;
        // only the form-data wipe stays here (it mutates the local dataWithWoonhuisData).
        const finalBA = dataWithWoonhuisData?.Contractdocument?.PP?.BA;
        const finalIP = dataWithWoonhuisData?.Contractdocument?.PP?.IP;
        const addressHasChanged = checkAddressChangedAndTrigger(finalBA, finalIP);

        // If address changed, clear old Infofolio data from the form
        if (addressHasChanged) {
          // Clear the Infofolio-filled fields so new data can be fetched
          dataWithWoonhuisData.Contractdocument.PP.BA.BA_BOUWJR = undefined;
          dataWithWoonhuisData.Contractdocument.PP.BA.BA_TYPEWON = undefined;
          dataWithWoonhuisData.Contractdocument.PP.BA.BA_AARDBW = undefined;
          dataWithWoonhuisData.Contractdocument.PP.BA.BA_AARDDAK = undefined;
          dataWithWoonhuisData.Contractdocument.PP.BA.BA_WONING = undefined;
          dataWithWoonhuisData.Contractdocument.PP.BA.BA_OPPWON = undefined;
          dataWithWoonhuisData.Contractdocument.PP.BA.BA_HERBWRD = undefined;
          // Also clear inboedelwaarde (IP_VERZSOM) if it was filled by Infofolio
          if (dataWithWoonhuisData.Contractdocument.PP.IP) {
            dataWithWoonhuisData.Contractdocument.PP.IP.IP_VERZSOM = undefined;
          }
          const strippedUiState = stripInfofolioUiState(dataWithWoonhuisData._uiState);
          if (strippedUiState) dataWithWoonhuisData._uiState = strippedUiState;
        }

        // Set required default: PP_BETTERM (payment term in months)
        if (!dataWithWoonhuisData.Contractdocument?.PP?.PP_BETTERM) {
          dataWithWoonhuisData.Contractdocument.PP.PP_BETTERM = 1;
        }

        // Set required default: PP_INGDAT (insurance start date in YYYYMMDD format)
        if (!dataWithWoonhuisData.Contractdocument?.PP?.PP_INGDAT) {
          const today = new Date();
          const year = today.getFullYear();
          const month = String(today.getMonth() + 1).padStart(2, '0');
          const day = String(today.getDate()).padStart(2, '0');
          const dateInN8Format = `${year}${month}${day}`;
          dataWithWoonhuisData.Contractdocument.PP.PP_INGDAT = Number(dateInN8Format);
        }
        const tarToday = new Date();
        dataWithWoonhuisData.Contractdocument.PP.PP_TARDAT = Number(
          `${tarToday.getFullYear()}${String(tarToday.getMonth() + 1).padStart(2, '0')}${String(tarToday.getDate()).padStart(2, '0')}`,
        );

        // PP_EXTERN (external product identifier)
        if (!dataWithWoonhuisData.Contractdocument.PP.PP_EXTERN) {
          dataWithWoonhuisData.Contractdocument.PP.PP_EXTERN = 'Inboedel';
        }

        // Always set BA_MONUMEN to 'N' (monument property - not supported in UI)
        dataWithWoonhuisData.Contractdocument.PP.BA.BA_MONUMEN = 'N';

        // Prefill Gezinssamenstelling from any value the user already picked on any product
        // (including one they later deselected) — it is entered once and shared across products.
        if (!dataWithWoonhuisData.Contractdocument?.PP?.IP?.IP_GSS) {
          const sharedGss = findSharedFamilyComposition(getStepData);
          if (sharedGss) {
            if (!dataWithWoonhuisData.Contractdocument.PP.IP) dataWithWoonhuisData.Contractdocument.PP.IP = {};
            dataWithWoonhuisData.Contractdocument.PP.IP.IP_GSS = sharedGss;
          }
        }

        // Set final data
        setData(dataWithWoonhuisData);

        // IMPORTANT: Save the linked data to workflow so it's available for Woonhuis
        // Clean unselected coverage premiums before saving
        const cleanedInitialData = cleanUnselectedCoveragePremiums(dataWithWoonhuisData);
        saveWorkflowData(SCREEN_ID, cleanedInitialData);

        setLoading(false);

        // Check if calculation data already exists (user navigated back to this page)
        // If premium data exists, show success state but don't block re-calculation
        const hasPremiumData = dataWithWoonhuisData?.Contractdocument?.PP?.PP_NTP;
        if (hasPremiumData) {
          setCalculationSuccess(true);
          lastCalculatedData.current = inboedelCalcInputSignature(existingData);
          restampCoveragePricesCache(inboedelPreviewCacheKey(existingData));
        }

        // Mark initial load as complete - auto-calculation useEffect will handle triggering
        // calculation when all required fields are filled (with proper debouncing)
        setTimeout(() => {
          initialLoadComplete.current = true;
          // Trigger auto-calculation check via state update (ref alone won't cause re-render)
          setShouldAutoCalculate(true);
        }, 100);

      } catch (err) {
        setError(err instanceof Error ? err.message : 'Unknown error');
        setLoading(false);
      }
    };

    loadSchema();
  // eslint-disable-next-line react-hooks/exhaustive-deps -- mount-only schema load; its inputs are read once
  }, []);

  // Seed the policyholder address (VP) from clientInfo once; the AddressSection reads data.VP.
  useEffect(() => {
    if (loading || !relationData?.clientInfo) return;
    const clientInfo = relationData.clientInfo;
    if (!clientInfo.postalCode && !clientInfo.houseNumber) return;
    setData((prev: any) => {
      const vp = prev?.Contractdocument?.PP?.VP;
      if (vp?.VP_PCODE && vp?.VP_STRAAT) return prev;
      return {
        ...prev,
        Contractdocument: {
          ...prev?.Contractdocument,
          PP: {
            ...prev?.Contractdocument?.PP,
            VP: {
              ...prev?.Contractdocument?.PP?.VP,
              VP_PCODE: clientInfo.postalCode || vp?.VP_PCODE || '',
              VP_HUISNR: clientInfo.houseNumber ? parseInt(String(clientInfo.houseNumber), 10) : (vp?.VP_HUISNR || ''),
              VP_TOEVOEG: clientInfo.houseNumberAddition || vp?.VP_TOEVOEG || '',
              VP_STRAAT: clientInfo.street || vp?.VP_STRAAT || '',
              VP_PLAATS: clientInfo.city || vp?.VP_PLAATS || '',
            },
          },
        },
      };
    });
  }, [loading, relationData?.clientInfo]);

  // Sync from shared address (single source of truth) — every change flows into data.VP (and
  // data.BA when the risk address equals the policyholder address) so the calc payload and the
  // AddressSection value stay aligned with whichever product last edited the policyholder address.
  useEffect(() => {
    // Only update local data state after loading completes
    if (!loading && (sharedAddress.BA_PCODE || sharedAddress.BA_HUISNR)) {
      setData((prevData: any) => {
        const currentBA = prevData?.Contractdocument?.PP?.BA || {};
        const currentVP = prevData?.Contractdocument?.PP?.VP || {};
        // Need-update covers BA AND VP — schema-load may have already aligned BA from
        // sharedAddress without touching VP, in which case BA is in sync but VP still
        // lacks VP_STRAAT/VP_PLAATS. Without this, the sync would skip and VP would
        // stay unvalidated even though sharedAddress has full data.
        const needsUpdate =
          (sharedAddress.BA_PCODE && currentBA.BA_PCODE !== sharedAddress.BA_PCODE) ||
          (sharedAddress.BA_HUISNR && currentBA.BA_HUISNR !== sharedAddress.BA_HUISNR) ||
          (sharedAddress.BA_TOEVOEG !== undefined && currentBA.BA_TOEVOEG !== sharedAddress.BA_TOEVOEG) ||
          (sharedAddress.BA_STRAAT && currentBA.BA_STRAAT !== sharedAddress.BA_STRAAT) ||
          (sharedAddress.BA_PLAATS && currentBA.BA_PLAATS !== sharedAddress.BA_PLAATS) ||
          (sharedAddress.BA_PCODE && currentVP.VP_PCODE !== sharedAddress.BA_PCODE) ||
          (sharedAddress.BA_STRAAT && currentVP.VP_STRAAT !== sharedAddress.BA_STRAAT) ||
          (sharedAddress.BA_PLAATS && currentVP.VP_PLAATS !== sharedAddress.BA_PLAATS);

        if (!needsUpdate) return prevData;

        // VP (policyholder) always syncs from sharedAddress. BA syncs only when the
        // risk address is the same as the policyholder; when it's different the user is
        // entering a separate risk address and we must not overwrite it.
        return {
          ...prevData,
          Contractdocument: {
            ...prevData.Contractdocument,
            PP: {
              ...prevData.Contractdocument?.PP,
              ...(risicoAdresGelijk === 'J' ? {
                BA: {
                  ...prevData.Contractdocument?.PP?.BA,
                  ...(sharedAddress.BA_PCODE && { BA_PCODE: sharedAddress.BA_PCODE }),
                  ...(sharedAddress.BA_HUISNR && { BA_HUISNR: sharedAddress.BA_HUISNR }),
                  ...(sharedAddress.BA_TOEVOEG !== undefined && { BA_TOEVOEG: sharedAddress.BA_TOEVOEG }),
                  ...(sharedAddress.BA_STRAAT && { BA_STRAAT: sharedAddress.BA_STRAAT }),
                  ...(sharedAddress.BA_PLAATS && { BA_PLAATS: sharedAddress.BA_PLAATS }),
                },
              } : {}),
              VP: {
                ...prevData.Contractdocument?.PP?.VP,
                ...(sharedAddress.BA_PCODE && { VP_PCODE: sharedAddress.BA_PCODE }),
                ...(sharedAddress.BA_HUISNR && { VP_HUISNR: Number(sharedAddress.BA_HUISNR) || 0 }),
                ...(sharedAddress.BA_TOEVOEG !== undefined && { VP_TOEVOEG: sharedAddress.BA_TOEVOEG }),
                ...(sharedAddress.BA_STRAAT && { VP_STRAAT: sharedAddress.BA_STRAAT }),
                ...(sharedAddress.BA_PLAATS && { VP_PLAATS: sharedAddress.BA_PLAATS }),
              },
            },
          },
        };
      });

      // After syncing the address from another screen (e.g. Personenauto), check
      // whether we need to (re)fetch Infofolio data. Mirrors the init-time logic
      // at the top of the load function (the address comes from sharedAddress here;
      // the has-data check reads the local BA/IP). Only when the risk address IS the
      // policyholder address: with a separate risk address (risicoAdresGelijk 'N') the
      // policyholder address must never be looked up into this product's BA/IP.
      if (risicoAdresGelijk === 'J') {
        checkAddressChangedAndTrigger(sharedAddress, data?.Contractdocument?.PP?.IP, data?.Contractdocument?.PP?.BA);
      }
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps -- sharedAddress is the trigger; data/risicoAdresGelijk are read at fire time, and the Infofolio check must not re-run on every keystroke
  }, [loading, sharedAddress]);

  // Validation: Show inline validation errors as user fills the form
  // This only adds/removes errors for specific fields, preserving other validation errors
  useEffect(() => {
    // Don't validate until initial load is complete
    if (!initialLoadComplete.current || loading) {
      return;
    }

    const ba = data?.Contractdocument?.PP?.BA;
    const ip = data?.Contractdocument?.PP?.IP;

    // Update validation errors for specific fields without clearing others
    setValidationErrors(prev => {
      const updated = { ...prev };

      // Postal code inline validation
      if (ba?.BA_PCODE) {
        const postalCodePattern = /^[0-9]{4}\s?[A-Z]{2}$/i;
        if (!postalCodePattern.test(ba.BA_PCODE)) {
          updated['Contractdocument.PP.BA.BA_PCODE'] = t('screens.inboedel.validation.postalCodeInvalid');
        } else {
          delete updated['Contractdocument.PP.BA.BA_PCODE'];
        }
      }

      // Surface area inline validation
      if (ba?.BA_OPPWON && ba.BA_OPPWON <= 0) {
        updated['Contractdocument.PP.BA.BA_OPPWON'] = t('screens.inboedel.validation.surfaceAreaInvalid');
      } else if (ba?.BA_OPPWON && ba.BA_OPPWON > 0) {
        delete updated['Contractdocument.PP.BA.BA_OPPWON'];
      }

      // Insured sum inline validation
      if (ip?.IP_VERZSOM && ip.IP_VERZSOM <= 0) {
        updated['Contractdocument.PP.IP.IP_VERZSOM'] = t('screens.inboedel.validation.insuredSumInvalid');
      } else if (ip?.IP_VERZSOM && ip.IP_VERZSOM > 0) {
        delete updated['Contractdocument.PP.IP.IP_VERZSOM'];
      }

      return updated;
    });
  }, [data, loading, t, initialLoadComplete]);


  // Handle custom field changes
  // Year-range validation for Bouwjaar (forwarded from WoningSpecificaties onBlur). The value is
  // already committed via handleCustomFieldChange onChange; this only sets/clears the error.
  const handleBuildingNumberBlur = (fieldPath: string, raw: string) => {
    if (fieldPath !== 'Contractdocument.PP.BA.BA_BOUWJR') return;
    const value = raw ? parseInt(raw, 10) : null;
    if (!value) {
      setValidationErrors(prev => ({ ...prev, [fieldPath]: t('screens.woonhuis.validation.constructionYearRequired') }));
      return;
    }
    const currentYear = new Date().getFullYear();
    if (value < 1800 || value > currentYear) {
      setValidationErrors(prev => ({ ...prev, [fieldPath]: t('screens.woonhuis.validation.constructionYearInvalid', { maxYear: currentYear }) }));
    } else {
      setValidationErrors(prev => { const u = { ...prev }; delete u[fieldPath]; return u; });
    }
  };

  const handleCustomFieldChange = (fieldPath: string, value: any) => {

    // Clear validation error for this field when user updates it
    if (validationErrors[fieldPath]) {
      setValidationErrors((prev) => {
        const updated = { ...prev };
        delete updated[fieldPath];
        return updated;
      });
    }

    // Leaving 'Vaste taxatie' hides (and clears) its sub-fields — drop their errors too
    if (fieldPath === 'Contractdocument.PP.IB.IB_INBVST' && value !== 'V') {
      setValidationErrors((prev) => {
        const updated = { ...prev };
        delete updated['Contractdocument.PP.BA.BA_EINDGTX'];
        delete updated['Supportsection.Divers.TaxatieVRT'];
        return updated;
      });
    }

    // BACKEND PROTECTION: Prevent address changes if address is locked (managed in Woonhuis)
    const addressFields = [
      'Contractdocument.PP.BA.BA_PCODE',
      'Contractdocument.PP.BA.BA_HUISNR',
      'Contractdocument.PP.BA.BA_TOEVOEG',
      'Contractdocument.PP.BA.BA_STRAAT',
      'Contractdocument.PP.BA.BA_PLAATS'
    ];

    if (isAddressLocked && addressFields.includes(fieldPath)) {
      return;
    }

    // BACKEND PROTECTION: Prevent building details changes if locked (managed in Woonhuis)
    // Note: BA_OPPWON and BA_BEVEIL are excluded as they are not known in Woonhuis
    // Building fields that are locked when filled from Woonhuis
    const buildingFields = [
      'Contractdocument.PP.BA.BA_TYPEWON',
      'Contractdocument.PP.BA.BA_WONING',
      'Contractdocument.PP.BA.BA_AARDBW',
      'Contractdocument.PP.BA.BA_AARDDAK',
      'Contractdocument.PP.BA.BA_BOUWJR',
      'Contractdocument.PP.BA.BA_OPPWON',
      'Contractdocument.PP.BA.BA_EIGENWO',
      'Contractdocument.PP.BA.BA_PERMBEW',
      'Contractdocument.PP.BA.BA_BDRFBEL',
      'Contractdocument.PP.BA.BA_OPZPKVA'
    ];

    if (isBuildingDetailsLocked && buildingFields.includes(fieldPath)) {
      return;
    }

    // BACKEND PROTECTION: Prevent Gezinssamenstelling change if locked (managed by Woonhuis, which comes before Inboedel)
    if (isGezinsamenstellingLocked && fieldPath === 'Contractdocument.PP.IP.IP_GSS') {
      return;
    }

    setData((prevData: any) =>
      applyInboedelFieldUpdate(prevData, fieldPath, value, {
        risicoAdresGelijk,
        selectedProducts,
        getStepData,
        saveStepData,
        saveWorkflowData,
        setIsMonumentProperty,
        setValidationErrors,
        screenId: SCREEN_ID,
      })
    );
  };


  // Building data belongs to the address it was fetched for: when the insured risk address changes,
  // drop the Infofolio-derived BA fields and the inboedelwaarde/vaststelling so the new address is
  // fetched afresh (the same reset syncRiskAddressToProperties applies to sibling products).
  //
  // Takes and returns the WHOLE step document, because clearing PP.BA alone does not survive the
  // next rebuild: mapToCalculationRequest and applyProductDefaults both restore BA_HERBWRD /
  // BA_BOUWJR / BA_OPPWON / BA_MONUMEN from the root `_uiState` stash whenever they are absent
  // from PP.BA, so the OLD address's rebuild value would be re-injected under the NEW address —
  // and Midglas rates glass over it. stripInfofolioUiState is the same helper the cross-product
  // path (syncAddressToOtherProducts) uses for exactly this reason.
  const resetInfofolioDerived = (doc: any) => {
    const pp = doc?.Contractdocument?.PP || {};
    const ba = { ...(pp.BA || {}) };
    for (const field of INFOFOLIO_DERIVED_BA_FIELDS) delete ba[field];
    const nextPP: any = { ...pp, BA: ba };
    if (pp.IP) { const { IP_VERZSOM: _verzsom, ...ip } = pp.IP; nextPP.IP = ip; }
    if (pp.IB) { const { IB_INBVST: _inbvst, ...ib } = pp.IB; nextPP.IB = ib; }
    const nextUiState = stripInfofolioUiState(doc?._uiState);
    return {
      ...doc,
      ...(nextUiState ? { _uiState: nextUiState } : {}),
      Contractdocument: { ...doc?.Contractdocument, PP: nextPP },
    };
  };

  // Infofolio follows the insured risk address: a resolved address that differs from the one the
  // building data was fetched for clears the locks and queues a fresh fetch (useInfofolio).
  const refetchForAddress = (a: Address) => {
    if (!a.street || !a.city) return;
    checkAddressChangedAndTrigger(addressAsBa(a), data?.Contractdocument?.PP?.IP, data?.Contractdocument?.PP?.BA);
    lastCalculatedData.current = '';
  };

  // Risk-address Ja/Nee answer. Shared across property products (they insure the same
  // property). 'J' => insured risk address equals the policyholder address (BA mirrors VP);
  // 'N' => a separate "Adres verzekerde woning" is collected. Switching to 'N' starts EMPTY
  // unless a sibling property product already supplied a risk address (then reuse it).
  const handleRiskAddressSameChange = (newValue: 'J' | 'N') => {
    setRisicoAdresGelijk(newValue);
    setRiskAddressSame(newValue);
    const upstreamRisk = newValue === 'N' ? findUpstreamRiskAddress(getStepData, SCREEN_ID) : null;
    const nextRisk = newValue === 'J' ? vpAddressAsBa(data?.Contractdocument?.PP?.VP) : (upstreamRisk || {});
    const changed = baIdentity(nextRisk) !== baIdentity(data?.Contractdocument?.PP?.BA);
    setData((prev: any) => {
      const pp = prev?.Contractdocument?.PP || {};
      const base = changed ? resetInfofolioDerived(prev) : prev;
      const basePP = base?.Contractdocument?.PP || {};
      const nextBA: any = { ...(basePP.BA || {}) };
      if (newValue === 'J') {
        Object.assign(nextBA, vpAddressAsBa(pp.VP));
      } else if (upstreamRisk) {
        Object.assign(nextBA, {
          BA_PCODE: upstreamRisk.BA_PCODE,
          BA_HUISNR: upstreamRisk.BA_HUISNR,
          BA_TOEVOEG: upstreamRisk.BA_TOEVOEG || '',
          BA_STRAAT: upstreamRisk.BA_STRAAT || '',
          BA_PLAATS: upstreamRisk.BA_PLAATS || '',
        });
      } else {
        delete nextBA.BA_PCODE;
        delete nextBA.BA_HUISNR;
        delete nextBA.BA_TOEVOEG;
        delete nextBA.BA_STRAAT;
        delete nextBA.BA_PLAATS;
      }
      const updated = {
        ...base,
        Supportsection: {
          ...base?.Supportsection,
          Divers: { ...base?.Supportsection?.Divers, RisicoAdresGelijk: newValue },
        },
        Contractdocument: {
          ...base?.Contractdocument,
          PP: { ...basePP, BA: nextBA },
        },
      };
      stageAddressSave(() => saveWorkflowData(SCREEN_ID, cleanUnselectedCoveragePremiums(updated)));
      return updated;
    });
    scheduleAddressSave();
    if (changed) setInfofolioFilledFields(new Set());
    lastCalculatedData.current = '';
    // Woonhuis answers the same question for the same building. getStepData returns the LIVE
    // context object, so build a new one instead of mutating in place.
    if (selectedProducts?.woonhuis) {
      const woonhuisData = getStepData('woonhuis');
      if (woonhuisData) {
        // On 'N' the risk address is by definition NOT the policyholder address, so a Woonhuis BA
        // that still mirrors its own VP is the address the user just rejected. Flipping the toggle
        // without clearing it leaves that mirrored address behind as a "risk address" — which
        // findUpstreamRiskAddress would hand straight back to this screen as a read-only summary
        // the user cannot edit. Its building data goes with it (it belonged to that address).
        const woonhuisPP = woonhuisData.Contractdocument?.PP;
        const clearsMirroredBA =
          newValue === 'N' && isSameBuilding(woonhuisPP?.BA, vpAddressAsBa(woonhuisPP?.VP));
        const clearedBA = clearsMirroredBA
          ? (() => {
              const ba: any = { ...woonhuisPP.BA };
              for (const f of ['BA_PCODE', 'BA_HUISNR', 'BA_TOEVOEG', 'BA_STRAAT', 'BA_PLAATS', ...INFOFOLIO_DERIVED_BA_FIELDS]) delete ba[f];
              return ba;
            })()
          : null;
        const clearedUiState = clearsMirroredBA ? stripInfofolioUiState(woonhuisData._uiState) : null;
        saveStepData('woonhuis', {
          ...woonhuisData,
          ...(clearedUiState ? { _uiState: clearedUiState } : {}),
          Supportsection: {
            ...woonhuisData.Supportsection,
            Divers: { ...woonhuisData.Supportsection?.Divers, RisicoAdresGelijk: newValue },
          },
          ...(clearedBA
            ? {
                Contractdocument: {
                  ...woonhuisData.Contractdocument,
                  PP: { ...woonhuisPP, BA: clearedBA },
                },
              }
            : {}),
        });
        // Fourth place the old address's building data lives: the Infofolio sessionStorage caches.
        // woonhuis/calc.ts linkInboedelAddress falls back to 'infofolio_BA_HERBWRD' when Inboedel's
        // BA no longer carries one (which is exactly the state we just created), and writes it to
        // both PP.BA.BA_HERBWRD and PP.OP.OP_VERZSOM — re-opening the glass gate for the address the
        // user just rejected. syncAddressToOtherProducts / syncRiskAddressToProperties clear these
        // keys in the same breath as the derived fields; this handler is not routed through either.
        if (clearsMirroredBA && typeof window !== 'undefined') {
          for (const key of INFOFOLIO_SESSION_KEYS) sessionStorage.removeItem(key);
        }
      }
    }
    if (nextRisk.BA_PCODE && nextRisk.BA_HUISNR && nextRisk.BA_STRAAT && nextRisk.BA_PLAATS) {
      refetchForAddress({
        postcode: String(nextRisk.BA_PCODE),
        houseNumber: String(nextRisk.BA_HUISNR),
        addition: nextRisk.BA_TOEVOEG || '',
        street: nextRisk.BA_STRAAT,
        city: nextRisk.BA_PLAATS,
      });
    }
  };

  // The risk-address Ja/Nee question, rendered inside the Adresgegevens card (above the postcode)
  // via AddressSection's topContent slot.
  const riskAddressQuestion = (
    <div style={styles.mb16}>
      <label style={styles.fieldLabel} htmlFor="risicoadres">
        {t('screens.woonhuis.fields.riskAddressSame')}
      </label>
      <div style={styles.radioGroup}>
        {(['J', 'N'] as const).map((optValue) => {
          const selected = risicoAdresGelijk === optValue;
          return (
            <div
              key={optValue}
              style={selected ? styles.radioOptionSelected : styles.radioOption}
              onClick={() => handleRiskAddressSameChange(optValue)}
            >
              <div style={selected ? styles.radioDotSelected : styles.radioDot}>
                {selected && <div style={styles.radioDotInner} />}
              </div>
              {optValue === 'J' ? t('common.yes') : t('common.no')}
            </div>
          );
        })}
      </div>
    </div>
  );

  // Policyholder (VP) address, emitted by the shared AddressSection. AddressSection owns the
  // postcode lookup, multiple-match chooser and validation; here we only persist the result into
  // VP (and into BA when the risk address equals it). Cross-product propagation is handled by the
  // BA propagation effect (J) and by AddressSection's own shared-address write.
  const handlePolicyholderAddressChange = (a: Address) => {
    const huisnrNum = Number(a.houseNumber);
    const vpHuisnr = isNaN(huisnrNum) ? 0 : huisnrNum;
    const same = risicoAdresGelijk === 'J';
    const changed = same && baIdentity(addressAsBa(a)) !== baIdentity(data?.Contractdocument?.PP?.BA);

    setData((prev: any) => {
      const base = changed ? resetInfofolioDerived(prev) : prev;
      const basePP = base?.Contractdocument?.PP || {};
      const updated = {
        ...base,
        Contractdocument: {
          ...base?.Contractdocument,
          PP: {
            ...basePP,
            VP: {
              ...basePP.VP,
              VP_PCODE: a.postcode,
              VP_HUISNR: vpHuisnr,
              VP_TOEVOEG: a.addition || '',
              VP_STRAAT: a.street || '',
              VP_PLAATS: a.city || '',
            },
            ...(same ? { BA: { ...basePP.BA, ...addressAsBa(a) } } : {}),
          },
        },
      };
      stageAddressSave(() => saveWorkflowData(SCREEN_ID, cleanUnselectedCoveragePremiums(updated)));
      return updated;
    });
    scheduleAddressSave();

    setValidationErrors(prev => {
      const u = { ...prev };
      delete u['Contractdocument.PP.BA.BA_PCODE'];
      delete u['Contractdocument.PP.BA.BA_HUISNR'];
      delete u['Contractdocument.PP.BA.BA_STRAAT'];
      return u;
    });

    // Refetch building data only when this address is the insured risk (J) and it resolved.
    if (same) refetchForAddress(a);
  };

  // Insured-property (risk) address, used only when RisicoAdresGelijk is 'N'. Writes BA only
  // (never VP), refetches Infofolio for the risk postcode, and shares the risk address across
  // property products via the dedicated risk slice. shareAddress is false on this AddressSection
  // so it never overwrites the policyholder sharedData.address.
  const handleRiskAddressChange = (a: Address) => {
    const riskBA = addressAsBa(a);
    const changed = baIdentity(riskBA) !== baIdentity(data?.Contractdocument?.PP?.BA);
    setData((prev: any) => {
      const base = changed ? resetInfofolioDerived(prev) : prev;
      const basePP = base?.Contractdocument?.PP || {};
      const updated = {
        ...base,
        Contractdocument: {
          ...base?.Contractdocument,
          PP: { ...basePP, BA: { ...basePP.BA, ...riskBA } },
        },
      };
      stageAddressSave(() => saveWorkflowData(SCREEN_ID, cleanUnselectedCoveragePremiums(updated)));
      return updated;
    });
    scheduleAddressSave();

    setValidationErrors(prev => {
      const u = { ...prev };
      delete u['Contractdocument.PP.BA.BA_PCODE'];
      delete u['Contractdocument.PP.BA.BA_HUISNR'];
      delete u['Contractdocument.PP.BA.BA_STRAAT'];
      return u;
    });

    updateRiskAddress(riskBA, SCREEN_ID);
    syncRiskAddressToProperties(SCREEN_ID, riskBA, selectedProducts, getStepData, saveStepData);
    refetchForAddress(a);
  };

  // Single propagation path for the POLICYHOLDER address: whenever the committed BA changes while
  // the risk address equals it ('J'), push it to the shared store and the other products. When the
  // risk address differs ('N') BA is the insured-property address and must NOT flow into
  // sharedAddress - that is owned by the dedicated risk slice instead.
  useEffect(() => {
    if (loading) return;
    if (risicoAdresGelijk !== 'J') return;
    const ba = data?.Contractdocument?.PP?.BA;
    if (!ba?.BA_PCODE || !ba?.BA_HUISNR) return;

    const key = baIdentity(ba);
    if (key === lastPropagatedAddress.current) return;

    // If the shared store already matches this identity, the value flowed in FROM sharedAddress
    // (via the sync effect above): record it and skip to avoid an echo loop.
    const sharedMatches =
      sharedAddress.BA_PCODE === ba.BA_PCODE &&
      String(sharedAddress.BA_HUISNR ?? '') === String(ba.BA_HUISNR) &&
      (sharedAddress.BA_TOEVOEG || '') === (ba.BA_TOEVOEG || '');
    if (sharedMatches) {
      lastPropagatedAddress.current = key;
      return;
    }

    lastPropagatedAddress.current = key;
    const fullAddress = {
      BA_PCODE: ba.BA_PCODE,
      BA_HUISNR: ba.BA_HUISNR,
      BA_TOEVOEG: ba.BA_TOEVOEG || '',
      BA_STRAAT: ba.BA_STRAAT || '',
      BA_PLAATS: ba.BA_PLAATS || '',
    };
    updateSharedAddress(fullAddress);
    syncAddressToOtherProducts(SCREEN_ID, fullAddress, selectedProducts, getStepData, saveStepData);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    loading,
    risicoAdresGelijk,
    data?.Contractdocument?.PP?.BA?.BA_PCODE,
    data?.Contractdocument?.PP?.BA?.BA_HUISNR,
    data?.Contractdocument?.PP?.BA?.BA_TOEVOEG,
    data?.Contractdocument?.PP?.BA?.BA_STRAAT,
    data?.Contractdocument?.PP?.BA?.BA_PLAATS,
  ]);

  // Smart Home (EC 5470) insured sum is a fixed choice: €3.000 (default) or €6.000. Update the EC
  // array entry and re-price (the BFF prices EC by EC_VERZSOM); reset the calc cache so the recalc
  // is not skipped.
  const handleSmarthomeSumChange = (value: number) => {
    const pp = data?.Contractdocument?.PP || {};
    const ec = Array.isArray(pp.EC) ? [...pp.EC] : [];
    const idx = ec.findIndex((e: any) => e?.EC_CODE === EC_CODE_SMARTHOME);
    if (idx >= 0) {
      ec[idx] = { ...ec[idx], EC_VERZSOM: value };
    } else {
      ec.push({ EC_CODE: EC_CODE_SMARTHOME, EC_VERZSOM: value, EC_ERB: 100, EC_PPRC: 20, EC_WPREMBP: '4' });
    }
    const updated = { ...data, Contractdocument: { ...data.Contractdocument, PP: { ...pp, EC: ec } } };
    lastCalculatedData.current = '';
    setData(updated);
    saveStepData(SCREEN_ID, updated);
    handleCalculate(updated);
  };

  // Get validation errors (without side effects)
  const getValidationErrors = useCallback((): Record<string, string> => {
    const entries = validateInboedel(data, {
      now: new Date(),
      isAddressLocked,
      isBuildingDetailsLocked,
      isGezinsamenstellingLocked,
      isGlassRebuildValueLocked: infofolioFilledFields.has('herbwrd'),
      glassAllowed,
    });
    return Object.fromEntries(
      Object.entries(entries).map(([field, e]) => [field, t(e.key, e.params as any) as string])
    );
  }, [data, t, isAddressLocked, isGezinsamenstellingLocked, isBuildingDetailsLocked, infofolioFilledFields, glassAllowed]);

  // Validate form fields (with side effect of setting errors)
  const validateForm = useCallback((): { isValid: boolean; errors: Record<string, string> } => {
    const errors = getValidationErrors();
    setValidationErrors(errors);

    if (Object.keys(errors).length > 0) {
      return { isValid: false, errors };
    }

    return { isValid: true, errors: {} };
  }, [getValidationErrors]);

  // Re-translate existing validation errors when language changes
  useEffect(() => {
    if (!initialLoadComplete.current || loading) {
      return;
    }

    // Only re-validate if there are existing errors (to update their translations)
    setValidationErrors(prevErrors => {
      if (Object.keys(prevErrors).length === 0) {
        return prevErrors;
      }
      // Re-validate to get translated messages
      const errors = getValidationErrors();
      return errors;
    });
  }, [t, getValidationErrors, loading, initialLoadComplete]);

  const handleNext = () => {
    // Block navigation if property is a monument
    if (isMonumentProperty) {
      // Scroll to the monument warning
      const monumentWarning = document.getElementById('monument-warning');
      if (monumentWarning) {
        monumentWarning.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
      return;
    }

    // Block navigation if home is (partially) rented out — inboedel cannot be insured
    if (isRentedProperty) {
      const rentedWarning = document.getElementById('rented-warning');
      if (rentedWarning) {
        rentedWarning.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
      return;
    }

    const { isValid, errors } = validateForm();
    if (!isValid) {
      scrollToFirstError(errors);
      return;
    }

    // Check if calculation has been done before allowing navigation
    if (!calculationSuccess) {
      const calcError = { '_calculation': t('screens.inboedel.validation.calculateFirst') };
      setValidationErrors(calcError);
      // Scroll to calculate button area
      window.scrollTo({ top: 0, behavior: 'smooth' });
      return;
    }

    // Capture current data and navigation target before async work
    const currentData = JSON.parse(JSON.stringify(data));
    const nextScreen = getNextScreen('/workflow/inboedel', selectedProducts);
    if (!nextScreen) return;

    // Run data calculation with only SELECTED coverages to get correct CL/VW records.
    // Uses .then() pattern (NOT async/await) to keep handleNext synchronous.
    // IMPORTANT: Does NOT call setData() to avoid triggering auto-calc useEffect.
    const requestBody = mapToSelectedCoveragesRequest(currentData, { glassDisabled: glassDisabledRef.current });

    const finalFingerprint = stableStringify(requestBody);
    if (finalFingerprint === sessionStorage.getItem('inboedel_final_payload')) {
      navigate(nextScreen);
      return;
    }

    setIsCalculating(true);

    bffClient.request<any>('POST', '/api/v1/productservice/calculate', requestBody)
      .then((result: any) => {
        let dataToSave = currentData;
        if (result?.output) {
          isUpdatingFromCalculation.current = true;
          const mappedData = mapFromCalculationResponse(result, currentData);
          // Only apply discount to saved data when applyToData is true
          if (inboedelDiscountRef.current.applyToData) {
            applyInboedelDiscount(mappedData?.Contractdocument?.PP, inboedelDiscountRef.current);
          }
          dataToSave = { ...currentData, ...mappedData };
        }
        const cleanedData = cleanUnselectedCoveragePremiums(dataToSave);
        saveWorkflowData(SCREEN_ID, cleanedData);
        sessionStorage.setItem('inboedel_final_payload', stableStringify(mapToSelectedCoveragesRequest(cleanedData, { glassDisabled: glassDisabledRef.current })));
      })
      .catch(() => {
        // Fallback: save current data without CL/VW (stripped by price calc)
        const cleanedData = cleanUnselectedCoveragePremiums(currentData);
        saveWorkflowData(SCREEN_ID, cleanedData);
      })
      .finally(() => {
        setIsCalculating(false);
        navigate(nextScreen);
      });
  };

  const handlePrevious = () => {
    // Clean unselected coverage premiums before saving
    const cleanedData = cleanUnselectedCoveragePremiums(data);
    saveWorkflowData(SCREEN_ID, cleanedData);
    const previousScreen = getPreviousScreen('/workflow/inboedel', selectedProducts);
    if (previousScreen) {
      navigate(previousScreen);
    }
  };

  // Step + product counters from the shared workflow-nav hook (handleNext/handlePrevious keep
  // their own getNextScreen/getPreviousScreen calls — navigation happens after async save work).
  const { currentStep, totalSteps, productIndex, totalProducts } = useWorkflowNav(SCREEN_ID);

  const contentsValueFromInfofolio = infofolioFilledFields.has('inboedelwaarde') && Number(data?.Contractdocument?.PP?.IP?.IP_VERZSOM) > 0;
  const valuationFromInfofolio = infofolioFilledFields.has('inbvst') && !!data?.Contractdocument?.PP?.IB?.IB_INBVST;
  const valuationField = valuationFromInfofolio ? null : (
    <div key="inbvst" style={styles.addressField}>
      <label style={styles.fieldLabel}>
        {t('screens.inboedel.fields.contentsValueDetermination')} <span style={styles.required}>*</span>
      </label>
      <select
        value={data?.Contractdocument?.PP?.IB?.IB_INBVST || ''}
        onChange={(e) => handleCustomFieldChange('Contractdocument.PP.IB.IB_INBVST', e.target.value)}
        className={validationErrors['Contractdocument.PP.IB.IB_INBVST'] ? 'field-error' : ''}
        data-error={validationErrors['Contractdocument.PP.IB.IB_INBVST'] ? 'true' : undefined}
        disabled={infofolioFilledFields.has('inbvst')}
        style={{
          ...styles.fieldSelect,
          ...(validationErrors['Contractdocument.PP.IB.IB_INBVST'] ? styles.fieldSelectError : {}),
          ...(infofolioFilledFields.has('inbvst') ? styles.fieldInputDisabled : {})
        }}
        required
      >
        <option value="">{t('screens.inboedel.placeholders.select')}</option>
        <option value="E">{t('screens.woonhuis.options.rebuildValue.ownerValuation')}</option>
        {data?.Contractdocument?.PP?.IB?.IB_INBVST === 'M' && (
          <option value="M" disabled>{t('screens.woonhuis.options.rebuildValue.companyValuation')}</option>
        )}
        <option value="V">{t('screens.woonhuis.options.rebuildValue.fixedValuation')}</option>
      </select>
      {validationErrors['Contractdocument.PP.IB.IB_INBVST'] && (
        <p style={styles.errorMessage}>{validationErrors['Contractdocument.PP.IB.IB_INBVST']}</p>
      )}
    </div>
  );

  // Inboedeldekking block only renders while something in it still needs the customer.
  const showCoverageSection = shouldShowCoverageSection({
    data,
    lockedFields: infofolioFilledFields,
    isGezinsamenstellingLocked,
    errors: validationErrors,
  });

  // Loading state
  if (loading) {
    return <LoadingScreen message={t('common.loadingSchema')} />;
  }

  // Error state
  if (error) {
    return (
      <div style={styles.errorContainer}>
        <h2 style={styles.errorTitle}>{t('common.errorLoadingSchema')}</h2>
        <p>{error}</p>
        <button onClick={() => navigate('/')} style={styles.errorButton}>
          {t('common.backToHome')}
        </button>
      </div>
    );
  }

  return (
    <WorkflowPageLayout currentStep={currentStep} totalSteps={totalSteps}>
      <main style={styles.page}>
        <div style={styles.container}>
          <WithHelpSidebar stackEarly>
            <header style={styles.contentHeader}>
              <h1 style={styles.title}>{t('products.inboedel.name')}</h1>
            </header>

            {calculationError && (
              <div style={styles.errorBanner}>
                <span>✗ {calculationError}</span>
              </div>
            )}

            {/* Form layout */}

            <div style={styles.formColumn}>
              {/* Adresgegevens: the policyholder address through the shared AddressSection (it owns
                  the lookup, the addition chooser and the validation); read-only when an upstream
                  selected product already supplied it. */}
              {shouldHideAddressSection('inboedel', selectedProducts, getStepData) ? (
                <AddressSection
                  readOnly
                  icon={Users}
                  title="screens.woonhuis.fields.policyholderAddress"
                  topContent={riskAddressQuestion}
                  value={{
                    postcode: sharedAddress?.BA_PCODE || data?.Contractdocument?.PP?.VP?.VP_PCODE || '',
                    houseNumber: sharedAddress?.BA_HUISNR != null ? String(sharedAddress.BA_HUISNR) : (data?.Contractdocument?.PP?.VP?.VP_HUISNR != null ? String(data.Contractdocument.PP.VP.VP_HUISNR) : ''),
                    addition: sharedAddress?.BA_TOEVOEG || data?.Contractdocument?.PP?.VP?.VP_TOEVOEG || '',
                    street: sharedAddress?.BA_STRAAT || data?.Contractdocument?.PP?.VP?.VP_STRAAT || '',
                    city: sharedAddress?.BA_PLAATS || data?.Contractdocument?.PP?.VP?.VP_PLAATS || '',
                  }}
                />
              ) : (
                <AddressSection
                  icon={Users}
                  title="screens.woonhuis.fields.policyholderAddress"
                  topContent={riskAddressQuestion}
                  value={{
                    postcode: data?.Contractdocument?.PP?.VP?.VP_PCODE || '',
                    houseNumber: data?.Contractdocument?.PP?.VP?.VP_HUISNR != null && data?.Contractdocument?.PP?.VP?.VP_HUISNR !== '' ? String(data.Contractdocument.PP.VP.VP_HUISNR) : '',
                    addition: data?.Contractdocument?.PP?.VP?.VP_TOEVOEG || '',
                    street: data?.Contractdocument?.PP?.VP?.VP_STRAAT || '',
                    city: data?.Contractdocument?.PP?.VP?.VP_PLAATS || '',
                  }}
                  onChange={handlePolicyholderAddressChange}
                />
              )}

              {/* Adres verzekerde woning (separate insured-property address when it differs).
                  Read-only summary when a sibling property product collected it; an editable
                  block when this screen did (or nobody yet). */}
              {risicoAdresGelijk === 'N' && (() => {
                const upstreamRisk = findSiblingRiskAddress(getStepData, SCREEN_ID, riskAddressSource);
                return upstreamRisk ? (
                  <AddressSection
                    key="risk-summary"
                    readOnly
                    icon={House}
                    shareAddress={false}
                    title="screens.woonhuis.fields.insuredPropertyAddress"
                    value={{
                      postcode: upstreamRisk.BA_PCODE || '',
                      houseNumber: upstreamRisk.BA_HUISNR != null ? String(upstreamRisk.BA_HUISNR) : '',
                      addition: upstreamRisk.BA_TOEVOEG || '',
                      street: upstreamRisk.BA_STRAAT || '',
                      city: upstreamRisk.BA_PLAATS || '',
                    }}
                  />
                ) : (
                  <AddressSection
                    key="risk-editable"
                    idPrefix="risk-address"
                    icon={House}
                    shareAddress={false}
                    title="screens.woonhuis.fields.insuredPropertyAddress"
                    value={{
                      postcode: data?.Contractdocument?.PP?.BA?.BA_PCODE || '',
                      houseNumber: data?.Contractdocument?.PP?.BA?.BA_HUISNR != null ? String(data.Contractdocument.PP.BA.BA_HUISNR) : '',
                      addition: data?.Contractdocument?.PP?.BA?.BA_TOEVOEG || '',
                      street: data?.Contractdocument?.PP?.BA?.BA_STRAAT || '',
                      city: data?.Contractdocument?.PP?.BA?.BA_PLAATS || '',
                    }}
                    onChange={handleRiskAddressChange}
                  />
                );
              })()}

          {/* Building Details Section - hidden entirely when Woonhuis already has building data */}
          {!isBuildingDetailsLocked && (
          <div style={styles.buildingSpecsContainer}>
            <div className="inboedel-building-header" style={{ ...styles.sectionHeader, ...styles.coverageHeaderRow }}>
              <div style={styles.rowCenter}>
                <div style={{ ...styles.sectionIconContainer, ...styles.mr12 }}>
                  <ClipboardText size={28} weight="duotone" color="var(--secondary-color, #F51C17)" style={styles.opacityFull} />
                </div>
                <h2 style={styles.addressTitle}>{t('screens.inboedel.sections.buildingSpecs')}</h2>
              </div>
              {isLoadingPropertyInfo && (
                <div className="inboedel-property-loading" style={styles.rowCenter8}>
                  <div style={styles.propertySpinner} />
                  <span style={styles.primaryLabel13}>
                    {t('common.fetchingPropertyDetails')}
                  </span>
                </div>
              )}
            </div>

            {/* Monument Warning - Show if property is detected as a monument */}
            {isMonumentProperty && (
              <UninsurableWarning
                id="monument-warning"
                titleKey="screens.inboedel.monumentWarning.title"
                messageKey="screens.inboedel.monumentWarning.message"
                contactInfo={contactInfo}
              />
            )}

            {/* Rented Warning - shown when BA_WONING is 'D' (deels verhuurd) or 'H' (verhuurd) */}
            {isRentedProperty && (
              <UninsurableWarning
                id="rented-warning"
                titleKey="screens.inboedel.rentedWarning.title"
                messageKey="screens.inboedel.rentedWarning.message"
                contactInfo={contactInfo}
              />
            )}

            {/* Building specifications — shared single-source component */}
            <WoningSpecificaties
              variant="inboedel"
              ba={data?.Contractdocument?.PP?.BA}
              styles={styles}
              onFieldChange={handleCustomFieldChange}
              lockedFields={infofolioFilledFields}
              errors={validationErrors}
              onNumberBlur={handleBuildingNumberBlur}
            />

          </div>
          )}

          {/* Rented Warning (standalone) - the coverage-section instance below is gone with the
              section; keep the single DOM instance when both placements are hidden. */}
          {!showCoverageSection && isRentedProperty && isBuildingDetailsLocked && (
            <UninsurableWarning
              id="rented-warning"
              titleKey="screens.inboedel.rentedWarning.title"
              messageKey="screens.inboedel.rentedWarning.message"
              contactInfo={contactInfo}
            />
          )}

          {/* Main Inboedel Coverage Section - hidden when Infofolio supplied value + vaststelling
              and nothing else in it needs input (see shouldShowCoverageSection). */}
          {showCoverageSection && (
          <div style={styles.sectionContainer}>
            <div style={styles.sectionHeader}>
              <div style={styles.sectionIconContainer}>
                <Users size={28} weight="duotone" color="var(--secondary-color, #F51C17)" style={styles.opacityFull} />
              </div>
              <h2 style={styles.sectionTitle}>{t('screens.inboedel.sections.coverage')}</h2>
            </div>

            {/* Rented Warning (coverage placement) - only when the building-details placement is hidden,
                so the warning is never rendered twice. Same id as the other instance: only one is in the DOM. */}
            {isRentedProperty && isBuildingDetailsLocked && (
              <UninsurableWarning
                id="rented-warning"
                titleKey="screens.inboedel.rentedWarning.title"
                messageKey="screens.inboedel.rentedWarning.message"
                contactInfo={contactInfo}
              />
            )}

            {/* Row 1: Inboedelwaarde + Vaststelling, only the ones Infofolio could not fill. The
                herbouwwaarde (the Midglas input for Glas) is Infofolio-only and never shown. */}
            {(() => {
              const fields = [
                !contentsValueFromInfofolio && (
                  <div key="verzsom" style={styles.addressField}>
                    <label style={styles.fieldLabel} htmlFor="verzsom">
                      {t('screens.inboedel.fields.insuredSum')} <span style={styles.required}>*</span>
                    </label>
                    <div style={styles.currencyInputContainer}>
                      <span style={styles.currencySymbol}>€</span>
                      <input
                        id="verzsom"
                        type="text"
                        value={formatCurrency(data?.Contractdocument?.PP?.IP?.IP_VERZSOM ?? '')}
                        onChange={(e) => {
                          const value = unformatCurrency(e.target.value) ?? 0;
                          handleCustomFieldChange('Contractdocument.PP.IP.IP_VERZSOM', value);
                        }}
                        style={{
                          ...styles.currencyInput,
                          ...(validationErrors['Contractdocument.PP.IP.IP_VERZSOM'] ? styles.fieldInputError : {}),
                        }}
                        placeholder={t('screens.inboedel.placeholders.insuredSum')}
                        inputMode="numeric"
                        required
                      />
                    </div>
                    {validationErrors['Contractdocument.PP.IP.IP_VERZSOM'] && (
                      <p style={styles.errorMessage}>{validationErrors['Contractdocument.PP.IP.IP_VERZSOM']}</p>
                    )}
                  </div>
                ),
                valuationField,
              ].filter(Boolean);
              if (fields.length === 0) return null;
              return (
                <div className="woonhuis-address-row">
                  {fields}
                  {fields.length === 1 && <div style={styles.addressField} aria-hidden="true" />}
                </div>
              );
            })()}

            {/* Row 2: Gezinssamenstelling, only while an earlier product has not locked it. */}
            {!isGezinsamenstellingLocked && (
            <div className="woonhuis-address-row">
              {(
                <div style={styles.addressField}>
                  <label style={styles.fieldLabel}>
                    {t('screens.inboedel.fields.familyComposition')} <span style={styles.required}>*</span>
                  </label>
                  <select
                    value={familyComposition || data?.Contractdocument?.PP?.IP?.IP_GSS || readFamilyComposition(data) || ''}
                    onChange={(e) => { handleCustomFieldChange('Contractdocument.PP.IP.IP_GSS', e.target.value); setFamilyComposition(e.target.value); }}
                    className={validationErrors['Contractdocument.PP.IP.IP_GSS'] ? 'field-error' : ''}
                    data-error={validationErrors['Contractdocument.PP.IP.IP_GSS'] ? 'true' : undefined}
                    style={{
                      ...styles.fieldSelect,
                      ...(validationErrors['Contractdocument.PP.IP.IP_GSS'] ? styles.fieldSelectError : {})
                    }}
                    required
                  >
                    <option value="">{t('screens.inboedel.placeholders.select')}</option>
                    <option value="A">{t('screens.inboedel.options.familyComposition.singleWithoutChildren')}</option>
                    <option value="H">{t('screens.inboedel.options.familyComposition.coupleWithoutChildren')}</option>
                    <option value="I">{t('screens.inboedel.options.familyComposition.coupleWithChildren')}</option>
                    <option value="K">{t('screens.inboedel.options.familyComposition.singleWithChildren')}</option>
                  </select>
                  {validationErrors['Contractdocument.PP.IP.IP_GSS'] && (
                    <p style={styles.errorMessage}>{validationErrors['Contractdocument.PP.IP.IP_GSS']}</p>
                  )}
                </div>
              )}
              <div style={styles.addressField} aria-hidden="true" />
            </div>

            )}

            {/* IP_GARONDV (garantie tegen onderverzekering) is derived from IB_INBVST and neither
                editable nor shown here. The All Risk box below carries the guarantee as product
                text regardless of the flag (product decision). */}

            {/* Row 3 (only for Vaste taxatie): Einddatum geldigheid taxatie + Taxatie door
                VRT-taxateur — required while 'V' is selected (mirrors WoonhuisScreen). */}
            {data?.Contractdocument?.PP?.IB?.IB_INBVST === 'V' && (
              <div className="woonhuis-address-row">
                <div style={styles.addressField}>
                  <label style={styles.fieldLabel} htmlFor="eindgtx">
                    {t('screens.woonhuis.fields.valuationEndDate')} <span style={styles.required}>*</span>
                  </label>
                  <DatePicker
                    id="eindgtx"
                    hasError={!!validationErrors['Contractdocument.PP.BA.BA_EINDGTX']}
                    value={(() => {
                      const val = data?.Contractdocument?.PP?.BA?.BA_EINDGTX;
                      if (!val) return '';
                      const s = String(val);
                      if (s.length === 8) return `${s.slice(6, 8)}-${s.slice(4, 6)}-${s.slice(0, 4)}`;
                      return '';
                    })()}
                    onChange={(dateStr: string) => {
                      if (dateStr && dateStr.length === 10) {
                        const parts = dateStr.split('-');
                        const numValue = Number(`${parts[2]}${parts[1]}${parts[0]}`);
                        handleCustomFieldChange('Contractdocument.PP.BA.BA_EINDGTX', numValue);
                      } else if (!dateStr) {
                        handleCustomFieldChange('Contractdocument.PP.BA.BA_EINDGTX', null);
                      }
                    }}
                    futureOnly={true}
                    placeholder="DD-MM-JJJJ"
                  />
                  {validationErrors['Contractdocument.PP.BA.BA_EINDGTX'] && (
                    <p style={styles.errorMessage}>{validationErrors['Contractdocument.PP.BA.BA_EINDGTX']}</p>
                  )}
                </div>
                <div style={styles.addressField}>
                  <label style={styles.fieldLabel} htmlFor="taxvrt">
                    {t('screens.woonhuis.fields.vrtValuation')} <span style={styles.required}>*</span>
                  </label>
                  <div style={styles.radioGroup} id="taxvrt">
                    {(['J', 'N'] as const).map((optValue) => {
                      const selected = (data?.Supportsection?.Divers?.TaxatieVRT || '') === optValue;
                      const hasError = !selected && !!validationErrors['Supportsection.Divers.TaxatieVRT'];
                      return (
                        <div
                          key={optValue}
                          style={selected ? styles.radioOptionSelected : (hasError ? styles.radioOptionError : styles.radioOption)}
                          onClick={() => handleCustomFieldChange('Supportsection.Divers.TaxatieVRT', optValue)}
                        >
                          <div style={selected ? styles.radioDotSelected : styles.radioDot}>
                            {selected && <div style={styles.radioDotInner} />}
                          </div>
                          {optValue === 'J' ? t('common.yes') : t('common.no')}
                        </div>
                      );
                    })}
                  </div>
                  {validationErrors['Supportsection.Divers.TaxatieVRT'] && (
                    <p style={styles.errorMessage}>{validationErrors['Supportsection.Divers.TaxatieVRT']}</p>
                  )}
                </div>
              </div>
            )}
          </div>
          )}

          {/* Optional Coverages Section (Dekking Opties) */}
          <div style={styles.sectionContainer}>
            <div style={styles.sectionHeader} className="inboedel-coverage-header">
              <div style={styles.rowCenter12Flex1}>
                <div style={styles.sectionIconContainer}>
                  <Shield size={28} weight="duotone" color="var(--secondary-color, #F51C17)" style={styles.opacityFull} />
                </div>
                <h2 style={styles.sectionTitle}>{t('screens.inboedel.sections.optionalCoverages')}</h2>
              </div>
              {(() => {
                // Total = sum of the per-section floored badges. Floor-each-then-sum keeps the
                // total in arithmetic agreement with the displayed badges (base €14,24 + HU €0,42 → €14,66)
                // — without this, single-floor-of-summed-BTPs gives a 1-cent drift like 14,67
                // that confuses customers comparing the breakdown to the total.
                const pp = data?.Contractdocument?.PP;
                const dekkingen = data?.Supportsection?.Dekkingen;
                // Base group (always included): IP + CY + BI + mobile EC, floored once together so
                // the "Inboedel All Risk" badge value matches what's added into the total here.
                const basePP: any = {};
                if (pp?.IP) basePP.IP = pp.IP;
                if (pp?.CY) basePP.CY = pp.CY;
                if (pp?.BI) basePP.BI = pp.BI;
                const baseMobileEc = getEc(pp, EC_CODE_MOBIEL);
                if (baseMobileEc) basePP.EC = [baseMobileEc];
                if (pp?.PP_PPRC !== undefined) basePP.PP_PPRC = pp.PP_PPRC;
                let totalPremium = computeInboedelDisplayPremium(
                  { Contractdocument: { PP: basePP } },
                  config as any,
                );
                // Add each selected optional coverage at its individually-floored badge value.
                if (SHOW_OPTIONAL_COVERAGES) {
                  if (dekkingen?.Glas === 'J') {
                    totalPremium += sectionDisplayPrice(pp?.GP?.GP_BTP ?? previewPp?.GP?.GP_BTP, 'GP', pp?.GP ?? previewPp?.GP);
                  }
                  if (dekkingen?.Lijfsieraden === 'J') {
                    totalPremium += sectionDisplayPrice(pp?.LS?.LS_BTP ?? previewPp?.LS?.LS_BTP, 'LS', pp?.LS ?? previewPp?.LS);
                  }
                  if (dekkingen?.Eigenaarsbelang === 'J') {
                    totalPremium += sectionDisplayPrice(pp?.OP?.OP_BTP ?? previewPp?.OP?.OP_BTP, 'OP', pp?.OP ?? previewPp?.OP);
                  }
                  if (dekkingen?.Huurdersbelang === 'J') {
                    totalPremium += sectionDisplayPrice(pp?.HU?.HU_BTP ?? previewPp?.HU?.HU_BTP, 'HU', pp?.HU ?? previewPp?.HU);
                  }
                  if (dekkingen?.Bijzonderebezittingen === 'J') {
                    totalPremium += sectionDisplayPrice(pp?.KB?.KB_BTP ?? previewPp?.KB?.KB_BTP, 'KB', pp?.KB ?? previewPp?.KB);
                  }
                  if (dekkingen?.Smarthome === 'J') {
                    const smartBtp = getEc(pp, EC_CODE_SMARTHOME)?.EC_BTP
                      ?? getEc(previewPp, EC_CODE_SMARTHOME)?.EC_BTP;
                    const smartEc = getEc(pp, EC_CODE_SMARTHOME) ?? getEc(previewPp, EC_CODE_SMARTHOME);
                    totalPremium += sectionDisplayPrice(smartBtp, 'EC', smartEc);
                  }
                }
                totalPremium = Math.round(totalPremium * 100) / 100;

                if (totalPremium > 0 && !isCalculating) {
                  return (
                    <span className="inboedel-total-badge" style={styles.totalBadge}>
                      {t('screens.inboedel.sections.total')}: €{formatPrice(totalPremium)}
                    </span>
                  );
                }
                return (
                  <span className="inboedel-total-badge" style={styles.totalBadge}>
                    {isCalculating ? (
                      <span className="price-spinner" />
                    ) : (
                      <>
                        <CurrencyEur size={20} weight="bold" />
                        <span>—</span>
                      </>
                    )}
                  </span>
                );
              })()}
            </div>

            {/* Hoofddekking - fixed to All Risk, Eigen risico fixed to 100 */}

            {/* Base Coverage - Always Enabled */}
            {(() => {
              // Use the ex-provision helper on a PP containing only base sections (IP + CY + BI + mobile EC).
              // Keeps "Inboedel All Risk" consistent with the section total and optional coverage badges below.
              const basePP: any = {};
              if (data?.Contractdocument?.PP?.IP) basePP.IP = data.Contractdocument.PP.IP;
              if (data?.Contractdocument?.PP?.CY) basePP.CY = data.Contractdocument.PP.CY;
              if (data?.Contractdocument?.PP?.BI) basePP.BI = data.Contractdocument.PP.BI;
              const mobileEcBase = getEc(data?.Contractdocument?.PP, EC_CODE_MOBIEL);
              if (mobileEcBase) basePP.EC = [mobileEcBase];
              if (data?.Contractdocument?.PP?.PP_PPRC !== undefined) basePP.PP_PPRC = data.Contractdocument.PP.PP_PPRC;
              const basePremium = computeInboedelDisplayPremium({ Contractdocument: { PP: basePP } }, config as any);
              const dekkingLabel = 'Inboedel All Risk';

              return (
                <>
                  <div style={styles.baseCoverageBox}>
                    <div style={styles.rowBetweenFull}>
                      <label style={styles.baseCoverageTitle}>
                        <span style={styles.rowCenter8}>
                          {dekkingLabel}
                        </span>
                      </label>
                      <div style={styles.rowCenter10Mr12}>
                        {basePremium > 0 && (
                          <span style={styles.baseCoverageBadge}>
                            €{formatPrice(basePremium)}
                          </span>
                        )}
                      </div>
                    </div>
                    <ul style={styles.baseCoverageList}>
                      {[1, 2, 3, 4, 5].map((n) => (
                        <li key={n} style={styles.baseCoverageBullet}>
                          <Check size={16} color="var(--primary-color, #056666)" style={styles.iconNudge} />
                          <span>{t(`screens.inboedel.productInfo.inboedelAllrisk.item${n}`)}</span>
                          {n === 1 && (
                            <InfoTooltip
                              title={t('screens.inboedel.productInfo.underinsuranceGuarantee.title')}
                              text={t('screens.inboedel.productInfo.underinsuranceGuarantee.text')}
                            />
                          )}
                        </li>
                      ))}
                    </ul>
                  </div>
                  {SHOW_OPTIONAL_COVERAGES && (
                    <div style={styles.fullDivider}></div>
                  )}
                </>
              );
            })()}

            {/* Optional Coverages Grid - 2 columns on desktop, stack on mobile */}
            {SHOW_OPTIONAL_COVERAGES && <div className="optional-coverages-grid">
              {/* Buitenshuis + Mobiele elektronica are always included in base; Cyber is
                  temporarily disabled; Verhuur and Onbeperkte nieuwwaarde are no longer offered.
                  Each remaining optional coverage renders through CoverageToggleCard, driven by
                  the INBOEDEL_OPTIONAL_COVERAGES descriptors in coverages.ts (Glas is hidden
                  unless inboedelGlassAllowed: config switch off, no selected product already
                  covering glass, Infofolio-known address). When ONLY the address is the blocker
                  the slot shows a "contact DFD" note: the frontend never fills the herbouwwaarde. */}
              {INBOEDEL_OPTIONAL_COVERAGES.map((descriptor) => (
                (descriptor.hiddenWhenGlassCovered && !glassAllowed) ? (
                  glassNeedsContact ? (
                    <div key={descriptor.key} style={styles.toggleRow}>
                      <div style={styles.colFull}>
                        <label style={styles.toggleLabel}>{t(descriptor.labelKey)}</label>
                        <p style={styles.coverageUnavailableText}>{t('screens.inboedel.glassUnavailable')}</p>
                      </div>
                    </div>
                  ) : null
                ) : (
                  <CoverageToggleCard
                    key={descriptor.key}
                    descriptor={descriptor}
                    data={data}
                    previewPp={previewPp}
                    sectionDisplayPrice={sectionDisplayPrice}
                    handleCustomFieldChange={handleCustomFieldChange}
                    handleSmarthomeSumChange={handleSmarthomeSumChange}
                    setValidationErrors={setValidationErrors}
                    validationErrors={validationErrors}
                    lastCalculatedData={lastCalculatedData}
                    formatCurrency={formatCurrency}
                    unformatCurrency={unformatCurrency}
                    infofolioFilledFields={infofolioFilledFields}
                  />
                )
              ))}
            </div>}
          </div>
            </div> {/* End formColumn */}

            <WorkflowNavButtons
              onBack={handlePrevious}
              onNext={handleNext}
              nextLabel={isCalculating ? 'common.calculating' : isMonumentProperty ? 'screens.inboedel.uninsurableMonument' : isRentedProperty ? 'screens.inboedel.uninsurableRented' : 'common.nextPlain'}
              nextDisabled={isMonumentProperty || isRentedProperty}
              nextLoading={isCalculating}
              center={
                <div style={styles.productCounter}>
                  {t('common.productCounter', { current: productIndex, total: totalProducts })}
                </div>
              }
            />
          </WithHelpSidebar>

          {/* Debug panel for input/output (collapsible) */}
          <DebugPanel
            input={lastCalculationInput}
            output={lastCalculationOutput}
            onRecalculate={() => {
              const { isValid, errors } = validateForm();
              if (!isValid) {
                scrollToFirstError(errors);
                return;
              }
              handleCalculate();
            }}
            calculating={isCalculating}
            calcDisabled={isCalculating || isMonumentProperty || isRentedProperty}
            calcLabel={isMonumentProperty ? t('screens.inboedel.uninsurableMonument') : isRentedProperty ? t('screens.inboedel.uninsurableRented') : undefined}
            validationErrors={validationErrors}
          />

        </div>
      </main>
    </WorkflowPageLayout>
  );
};

// Use centralized common styles and add page-specific styles

export default InboedelScreen;
